<script setup>
import { ref, computed, watch } from 'vue'
import axios from '../axios'

const props = defineProps({
  visible: { type: Boolean, default: false },
  leagueId: { type: [String, Number], default: null },
  leagueName: { type: String, default: '' }
})

const emit = defineEmits(['close', 'select-fixture'])

const fixtures = ref([])
const loading = ref(false)
const baseDate = ref(new Date())
const monthsToShow = ref(1)
const monthOptions = [1, 2, 3, 4, 6]
const dateMode = ref('gamedate')

const months = ['January','February','March','April','May','June','July','August','September','October','November','December']
const weekDays = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']

const visibleMonths = computed(() => {
  const result = []
  for (let i = 0; i < monthsToShow.value; i++) {
    const d = new Date(baseDate.value.getFullYear(), baseDate.value.getMonth() + i, 1)
    result.push({ year: d.getFullYear(), month: d.getMonth() })
  }
  return result
})

const parseDate = (f) => {
  if (dateMode.value === 'created_at') {
    if (!f.created_at) return null
    return f.created_at.split('T')[0].split(' ')[0]
  }
  if (f.gamedate_db) return f.gamedate_db.split(' ')[0]
  if (!f.gamedate) return null
  const parts = f.gamedate.split(' ')[0].split('.')
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`
  return f.gamedate.split(' ')[0]
}

const fixturesByDate = computed(() => {
  const map = {}
  for (const f of fixtures.value) {
    const date = parseDate(f)
    if (!date) continue
    if (!map[date]) map[date] = []
    map[date].push(f)
  }
  return map
})

const getCalendarWeeks = (year, month) => {
  const weeks = []
  let firstDay = new Date(year, month, 1).getDay()
  firstDay = firstDay === 0 ? 6 : firstDay - 1
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  let week = new Array(firstDay).fill(null)
  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d)
    if (week.length === 7) {
      weeks.push(week)
      week = []
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null)
    weeks.push(week)
  }
  return weeks
}

const dateKey = (year, month, day) => {
  return `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
}

const getFixturesForDay = (year, month, day) => {
  if (!day) return []
  return fixturesByDate.value[dateKey(year, month, day)] || []
}

const isToday = (year, month, day) => {
  if (!day) return false
  const today = new Date()
  return today.getFullYear() === year && today.getMonth() === month && today.getDate() === day
}

const isPastDay = (year, month, day) => {
  if (!day) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const d = new Date(year, month, day)
  return d < today
}

const formatTime = (fixture) => {
  const gd = fixture.gamedate_db || fixture.gamedate
  if (!gd) return ''
  const parts = gd.split(' ')
  if (parts.length < 2) return ''
  return parts[1].substring(0, 5)
}

const truncate = (str, len) => {
  if (!str) return '—'
  return str.length > len ? str.substring(0, len) + '..' : str
}

const prevMonths = () => {
  const d = new Date(baseDate.value)
  d.setMonth(d.getMonth() - monthsToShow.value)
  baseDate.value = d
}

const nextMonths = () => {
  const d = new Date(baseDate.value)
  d.setMonth(d.getMonth() + monthsToShow.value)
  baseDate.value = d
}

const goToToday = () => {
  baseDate.value = new Date()
}

const fetchFixtures = async () => {
  if (!props.leagueId) return
  loading.value = true
  try {
    const { data } = await axios.get(`/api/v2/scheduled-games-by-league/${props.leagueId}`)
    fixtures.value = Array.isArray(data) ? data : []
  } catch (err) {
    console.error('Failed to fetch fixtures for calendar:', err)
    fixtures.value = []
  } finally {
    loading.value = false
  }
}

watch(() => props.visible, (isVisible) => {
  if (isVisible) {
    baseDate.value = new Date()
    fetchFixtures()
  }
})

const searchQuery = ref('')

const filteredFixturesByDate = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return fixturesByDate.value
  const map = {}
  for (const [date, fxs] of Object.entries(fixturesByDate.value)) {
    const matched = fxs.filter(f => {
      const id = String(f.id)
      const home = (f.home_team?.team_name || '').toLowerCase()
      const away = (f.away_team?.team_name || '').toLowerCase()
      return id.includes(q) || home.includes(q) || away.includes(q)
    })
    if (matched.length > 0) map[date] = matched
  }
  return map
})

const getFilteredFixturesForDay = (year, month, day) => {
  if (!day) return []
  return filteredFixturesByDate.value[dateKey(year, month, day)] || []
}

const totalFixtures = computed(() => {
  let count = 0
  for (const fxs of Object.values(filteredFixturesByDate.value)) count += fxs.length
  return count
})
const totalDays = computed(() => Object.keys(filteredFixturesByDate.value).length)

const isOldGame = (f) => {
  const d = parseDate(f)
  if (!d) return false
  const today = new Date().toISOString().split('T')[0]
  return d < today
}

const getFixtureStatusColor = (f) => {
  const old = isOldGame(f)
  if (f.auto_suspended_at) return old ? 'border-red-300/60 bg-gray-50 opacity-60' : 'border-red-400 bg-red-50/60'
  if (f.settlement_at) return old ? 'border-blue-300/60 bg-gray-50 opacity-60' : 'border-blue-400 bg-blue-50/60'
  if (f.published_at) return old ? 'border-gray-300 bg-gray-50 opacity-60' : 'border-green-400 bg-green-50/60'
  return old ? 'border-gray-200 bg-gray-50 opacity-50' : 'border-gray-200 bg-white'
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="fixed inset-0 z-[9999] flex items-stretch justify-center"
    >
      <div class="absolute inset-0 bg-black/60" @click="emit('close')"></div>

      <div class="relative bg-white shadow-2xl w-full h-full flex flex-col overflow-hidden">
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-3 border-b bg-gray-50 flex-shrink-0">
          <div class="flex items-center gap-4">
            <div class="flex items-center gap-2">
              <i class="fas fa-calendar-alt text-pink-500 text-lg"></i>
              <h2 class="text-lg font-bold text-gray-800">Fixture Calendar</h2>
            </div>
            <span v-if="leagueName" class="text-sm text-gray-600 bg-gray-200 px-3 py-1 rounded-full font-medium">{{ leagueName }}</span>
            <span class="text-sm text-gray-400">{{ totalFixtures }} fixtures / {{ totalDays }} days</span>
          </div>

          <div class="flex items-center gap-2">
            <!-- Search -->
            <div class="relative mr-2">
              <i class="fas fa-search text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 text-xs"></i>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search ID or team..."
                class="pl-8 pr-8 py-1.5 text-sm border rounded-lg w-52 focus:outline-none focus:ring-1 focus:ring-pink-400 focus:border-pink-400"
              />
              <button
                v-if="searchQuery"
                @click="searchQuery = ''"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <i class="fas fa-times text-xs"></i>
              </button>
            </div>

            <!-- Date mode toggle -->
            <div class="flex items-center bg-white border rounded-lg overflow-hidden mr-2">
              <button
                @click="dateMode = 'gamedate'"
                class="px-2.5 py-1.5 text-xs font-medium transition flex items-center gap-1"
                :class="dateMode === 'gamedate' ? 'bg-gray-700 text-white' : 'text-gray-500 hover:bg-gray-100'"
              ><i class="fas fa-gamepad text-[9px]"></i> Game</button>
              <button
                @click="dateMode = 'created_at'"
                class="px-2.5 py-1.5 text-xs font-medium transition flex items-center gap-1"
                :class="dateMode === 'created_at' ? 'bg-gray-700 text-white' : 'text-gray-500 hover:bg-gray-100'"
              ><i class="fas fa-plus-circle text-[9px]"></i> Created</button>
            </div>

            <!-- Months selector -->
            <div class="flex items-center bg-white border rounded-lg overflow-hidden mr-2">
              <button
                v-for="n in monthOptions"
                :key="n"
                @click="monthsToShow = n"
                class="px-3 py-1.5 text-xs font-medium transition"
                :class="monthsToShow === n
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-500 hover:bg-gray-100'"
              >{{ n }}m</button>
            </div>

            <button @click="prevMonths" class="px-3 py-1.5 text-sm bg-white border rounded-lg hover:bg-gray-50 transition font-medium">
              <i class="fas fa-chevron-left mr-1"></i> Prev
            </button>
            <button @click="goToToday" class="px-3 py-1.5 text-sm bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition font-medium">
              Today
            </button>
            <button @click="nextMonths" class="px-3 py-1.5 text-sm bg-white border rounded-lg hover:bg-gray-50 transition font-medium">
              Next <i class="fas fa-chevron-right ml-1"></i>
            </button>
            <button @click="emit('close')" class="ml-3 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition">
              <i class="fas fa-times text-lg"></i>
            </button>
          </div>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-auto p-4">
          <div v-if="loading" class="flex items-center justify-center h-64 text-gray-400">
            <i class="fas fa-spinner fa-spin text-3xl mr-3"></i>
            <span class="text-lg">Loading fixtures...</span>
          </div>

          <div v-else class="grid gap-4" :style="{ gridTemplateColumns: `repeat(${monthsToShow}, 1fr)`, height: 'calc(100%)' }">
            <div v-for="vm in visibleMonths" :key="`${vm.year}-${vm.month}`" class="border rounded-lg overflow-hidden flex flex-col">
              <!-- Month header -->
              <div class="bg-gray-700 text-white px-4 py-2 text-center font-bold text-sm flex-shrink-0">
                {{ months[vm.month] }} {{ vm.year }}
              </div>

              <!-- Weekday headers -->
              <div class="grid grid-cols-7 bg-gray-100 border-b flex-shrink-0">
                <div v-for="wd in weekDays" :key="wd" class="text-center text-[10px] font-semibold text-gray-500 py-1">
                  {{ wd }}
                </div>
              </div>

              <!-- Calendar weeks -->
              <div class="flex-1 grid grid-rows-6 divide-y min-h-0">
                <div
                  v-for="(week, wi) in getCalendarWeeks(vm.year, vm.month)"
                  :key="wi"
                  class="grid grid-cols-7 min-h-0"
                >
                  <div
                    v-for="(day, di) in week"
                    :key="di"
                    class="border-r last:border-r-0 p-1 text-xs overflow-y-auto fixture-scroll"
                    :class="{
                      'bg-gray-50/50': !day,
                      'bg-gray-100/80': day && isPastDay(vm.year, vm.month, day) && getFilteredFixturesForDay(vm.year, vm.month, day).length === 0,
                      'bg-gray-100': day && isPastDay(vm.year, vm.month, day) && getFilteredFixturesForDay(vm.year, vm.month, day).length > 0,
                      'bg-pink-50/40': day && !isPastDay(vm.year, vm.month, day) && getFilteredFixturesForDay(vm.year, vm.month, day).length > 0,
                    }"
                  >
                    <template v-if="day">
                      <div class="flex items-center justify-between mb-0.5">
                        <span
                          class="inline-flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-semibold"
                          :class="{
                            'bg-pink-500 text-white': isToday(vm.year, vm.month, day),
                            'text-gray-400': !isToday(vm.year, vm.month, day) && isPastDay(vm.year, vm.month, day),
                            'text-gray-700': !isToday(vm.year, vm.month, day) && !isPastDay(vm.year, vm.month, day),
                          }"
                        >{{ day }}</span>
                        <span
                          v-if="getFilteredFixturesForDay(vm.year, vm.month, day).length > 0"
                          class="text-[9px] bg-pink-500 text-white rounded-full px-1 py-0.5 font-bold leading-none"
                        >{{ getFilteredFixturesForDay(vm.year, vm.month, day).length }}</span>
                      </div>

                      <div class="space-y-0.5">
                        <div
                          v-for="f in getFilteredFixturesForDay(vm.year, vm.month, day)"
                          :key="f.id"
                          @click="emit('select-fixture', f.id)"
                          class="border rounded px-1 py-0.5 cursor-pointer hover:border-pink-400 transition group leading-tight"
                          :class="getFixtureStatusColor(f)"
                        >
                          <!-- Row 1: time + id + status icons -->
                          <div class="flex items-center gap-1 flex-wrap">
                            <span class="text-[9px] text-gray-400 flex-shrink-0">{{ formatTime(f) }}</span>
                            <span class="text-[9px] text-pink-500 font-semibold flex-shrink-0 group-hover:text-pink-600">#{{ f.id }}</span>
                            <span class="flex items-center gap-0.5 ml-auto">
                              <i
                                v-if="f.published_at"
                                class="fas fa-check-circle text-[8px] text-green-500"
                                title="Published"
                              ></i>
                              <i
                                v-else
                                class="fas fa-eye-slash text-[8px] text-gray-300"
                                title="Not published"
                              ></i>
                              <i
                                v-if="f.will_be_inplay"
                                class="fas fa-broadcast-tower text-[8px] text-blue-500"
                                title="Will be inplay"
                              ></i>
                              <i
                                v-if="f.auto_suspended_at"
                                class="fas fa-ban text-[8px] text-red-500"
                                title="Auto suspended"
                              ></i>
                              <i
                                v-if="f.settlement_at"
                                class="fas fa-flag-checkered text-[8px] text-purple-500"
                                title="Settled"
                              ></i>
                              <i
                                v-if="f.settlement_auto_check_at"
                                class="fas fa-clock text-[8px] text-amber-500"
                                title="Settlement auto-check scheduled"
                              ></i>
                            </span>
                          </div>
                          <!-- Row 2: teams -->
                          <div class="text-[10px] text-gray-700 truncate">
                            {{ truncate(f.home_team?.team_name, monthsToShow <= 2 ? 18 : 12) }}
                            <span class="text-gray-400">v</span>
                            {{ truncate(f.away_team?.team_name, monthsToShow <= 2 ? 18 : 12) }}
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer legend -->
        <div class="flex items-center gap-5 px-6 py-2.5 border-t bg-gray-50 text-[11px] text-gray-500 flex-shrink-0 flex-wrap">
          <div class="flex items-center gap-1.5">
            <span class="w-2.5 h-2.5 rounded-full bg-pink-500 inline-block"></span>
            Today
          </div>
          <div class="flex items-center gap-1.5">
            <span class="w-2.5 h-2.5 rounded bg-pink-50 border border-pink-200 inline-block"></span>
            Has fixtures
          </div>
          <div class="border-l pl-5 flex items-center gap-1.5">
            <i class="fas fa-check-circle text-green-500 text-[9px]"></i>
            Published
          </div>
          <div class="flex items-center gap-1.5">
            <i class="fas fa-eye-slash text-gray-300 text-[9px]"></i>
            Not published
          </div>
          <div class="flex items-center gap-1.5">
            <i class="fas fa-broadcast-tower text-blue-500 text-[9px]"></i>
            Inplay
          </div>
          <div class="flex items-center gap-1.5">
            <i class="fas fa-ban text-red-500 text-[9px]"></i>
            Suspended
          </div>
          <div class="flex items-center gap-1.5">
            <i class="fas fa-flag-checkered text-purple-500 text-[9px]"></i>
            Settled
          </div>
          <div class="flex items-center gap-1.5">
            <i class="fas fa-clock text-amber-500 text-[9px]"></i>
            Auto-check
          </div>
          <div class="border-l pl-5 flex items-center gap-1.5">
            <span class="w-2.5 h-2.5 rounded border-2 border-green-400 bg-green-50 inline-block"></span>
            Published
          </div>
          <div class="flex items-center gap-1.5">
            <span class="w-2.5 h-2.5 rounded border-2 border-red-400 bg-red-50 inline-block"></span>
            Suspended
          </div>
          <div class="flex items-center gap-1.5">
            <span class="w-2.5 h-2.5 rounded border-2 border-blue-400 bg-blue-50 inline-block"></span>
            Settled
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.fixture-scroll::-webkit-scrollbar {
  width: 3px;
}
.fixture-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.fixture-scroll::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}
.fixture-scroll::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
